import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { NumberInputComponent, VALIDATION_SERVICE } from '@luzula/luzula-lib';
import {
	FILZ_AB_OL_TEXT, FlurRichtung, UEBER_FLUR_TEXT, UNTER_FLUR_TEXT,
	abFlur, abMineralboden, betragAus, richtungAus, wertAus,
} from '../../flur-tiefe';

/** Steht in `eigenerWert`, solange kein eigener Wert unterwegs ist. */
const KEINER = Symbol('kein eigener Wert');

/**
 * Ein Zahlenfeld für eine Tiefe, die auch über Flur liegen kann (Spiegeltiefe,
 * Nutzer-Auftrag 02.10.2026). Im Feld steht der Betrag ab Flur, der Knopf im
 * Feld sagt „unter Flur" oder „über Flur"; nach aussen geht EINE Zahl ab
 * Oberkante Mineralboden, über Flur negativ (Regeln in flur-tiefe.ts).
 *
 * Bei einem Filz ist Flur die Oberkante Ol: `versatz` ist die Auflage
 * (filz-tiefen.ts), und das Feld rechnet sie hin und zurück. Die Beschriftung
 * sagt es dann, denn die übrigen Tiefen werden bei Filz ab Mineralboden
 * eingegeben.
 *
 * Der Knopf ist gesperrt, wenn das Feld es ist (berechnet, Kommentier-Modus):
 * Sonst liesse sich ueber ihn ein Wert aendern, den das Feld schuetzt.
 */
@Component({
	standalone: true,
	selector: 'bp-flur-tiefe-input',
	imports: [NumberInputComponent],
	template: `
		<wd-number-input [name]="name" [label]="beschriftung()" [model]="betrag" (modelChange)="betragGeaendert($event)">
			<button feldKnopf type="button" class="btn btn-outline-secondary feld-knopf flur-knopf"
				[class.active]="richtung === 'ueber'"
				[attr.aria-pressed]="richtung === 'ueber'"
				[disabled]="gesperrt()"
				title="Unter oder über Flur - antippen zum Umschalten"
				(click)="umschalten()">{{ richtung === 'ueber' ? UEBER_FLUR_TEXT : UNTER_FLUR_TEXT }}</button>
		</wd-number-input>`,
	styles: [':host { display: block; }'],
})
export class FlurTiefeInputComponent implements OnChanges {
	@Input() name!: string;
	@Input() label?: string;
	/** Die gespeicherte Zahl in cm ab Oberkante Mineralboden, über Flur negativ. */
	@Input() model: number | null | undefined;
	/** Bei einem Filz die Auflage in cm (`filzVersatz`), sonst 0. */
	@Input() versatz = 0;
	@Output() modelChange = new EventEmitter<number | null | undefined>();

	/** Statisch, damit die Sperre schon beim ersten Zeichnen feststeht. */
	@ViewChild(NumberInputComponent, { static: true }) feld?: NumberInputComponent;

	private vs = inject(VALIDATION_SERVICE);

	readonly UNTER_FLUR_TEXT = UNTER_FLUR_TEXT;
	readonly UEBER_FLUR_TEXT = UEBER_FLUR_TEXT;

	/** Wie der Knopf steht. Bei leerem Feld gilt sie für die nächste Eingabe. */
	richtung: FlurRichtung = 'unter';

	/**
	 * Der Wert, den dieses Feld zuletzt selbst gemeldet hat. Kommt er über die
	 * Bindung zurück, bleibt der Knopf, wie er steht; jeder andere Wert kommt
	 * von aussen (anderer Punkt, Übernahme) und stellt ihn neu.
	 */
	private eigenerWert: number | null | undefined | typeof KEINER = KEINER;

	/** Die Tiefe ab Flur, mit Vorzeichen (über Flur negativ). */
	private abFlur(): number | null | undefined {
		return abFlur(this.model, this.versatz);
	}

	/** Was im Feld steht: der Betrag ab Flur. Das Zahlenfeld kennt „leer" nur als undefined. */
	get betrag(): number | undefined {
		return betragAus(this.abFlur()) ?? undefined;
	}

	beschriftung(): string | undefined {
		return this.versatz > 0 && this.label ? `${this.label}, ${FILZ_AB_OL_TEXT}` : this.label;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['model']) {
			if (this.eigenerWert !== KEINER && this.model === this.eigenerWert) {
				this.eigenerWert = KEINER;
				return;
			}
			this.eigenerWert = KEINER;
			this.richtung = richtungAus(this.abFlur(), 'unter');
		} else if (changes['versatz']) {
			// Andere Auflage: Derselbe Spiegel kann jetzt auf der anderen Seite von Flur liegen.
			this.richtung = richtungAus(this.abFlur(), this.richtung);
		}
	}

	gesperrt(): boolean {
		return !!this.feld?.disabled();
	}

	/** Das Feld meldet einen Betrag - auch, wenn es nur den eigenen Wert wiederholt. */
	betragGeaendert(betrag: number | null | undefined): void {
		const tiefeAbFlur = wertAus(betrag, this.richtung);
		const neu = abMineralboden(tiefeAbFlur, this.versatz);
		if (neu === this.model || (neu == null && this.model == null)) return;
		this.richtung = richtungAus(tiefeAbFlur, this.richtung);
		this.melde(neu);
	}

	umschalten(): void {
		if (this.gesperrt()) return;
		this.richtung = this.richtung === 'ueber' ? 'unter' : 'ueber';
		const betrag = this.betrag;
		// Ohne Zahl gilt der Knopf nur für die nächste Eingabe; zu speichern gibt es nichts.
		if (typeof betrag !== 'number' || !Number.isFinite(betrag) || betrag === 0) return;
		this.melde(abMineralboden(wertAus(betrag, this.richtung), this.versatz));
		// Ein Knopf ist kein Feld: Den Speichern-Knopf muss er selbst wecken.
		this.vs.dirty.next(true);
	}

	private melde(wert: number | null | undefined): void {
		this.eigenerWert = wert;
		this.model = wert;
		this.modelChange.emit(wert);
	}
}
